import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { IsNotEmpty, IsOptional, IsString, Matches } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GetRolesCollaboration } from '@/modules/recognition/admin/application/collaborations/get-roles.collaboration';
import { GetScopesCollaboration } from '@/modules/recognition/admin/application/collaborations/get-scopes.collaboration';
import type {
  CreateSSORegistrationDto,
  RoleDto,
  ScopeDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import { CreateSSORegistrationCollaboration } from '../../../application/collaborations/sso/create-sso-registration.collaboration';

class CreateSSORegistrationForm {
  @IsNotEmpty({ message: 'Campo obrigatório' })
  provider: string;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  role: RoleDto;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  scope: ScopeDto;

  @IsString()
  @IsOptional()
  @Matches(/^[a-z0-9]+(?:-[a-z0-9]+)*[-a-z0-9]*\*?$/, {
    message:
      'O nome da tag deve conter apenas letras minúsculas, números, hífens e asteriscos(*) no final da tag',
  })
  tag: string;

  reset(): void {
    this.provider = '';
    this.role = null;
    this.scope = null;
  }
}

const providers = [
  {
    slug: 'natura',
    name: 'Natura',
  },
];

type ProviderDto = (typeof providers)[0];

@ViewModel({ singleton: true })
export class CreateSSORegistrationViewModel implements IReactiveModel {
  @observable form = new ObservableForm(CreateSSORegistrationForm);

  @observable roles: RoleDto[] = [];
  @observable scopes: ScopeDto[] = [];
  @observable providers: ProviderDto[] = providers;

  @observable isLoading = false;
  @observable hasError = false;

  @observable isSubmitting = false;

  private navigate: NavigateFunction;

  constructor(
    private getRolesCollaboration: GetRolesCollaboration,
    private getScopesCollaboration: GetScopesCollaboration,
    private createSSORegistrationCollaboration: CreateSSORegistrationCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit({ navigate }: { navigate: NavigateFunction }) {
    try {
      this.navigate = navigate;

      const [{ data: roles }, { data: scopes }] = await Promise.all([
        this.getRolesCollaboration.run(),
        this.getScopesCollaboration.run(),
      ]);

      // Remove admin role and Descomplica IO scope
      this.roles = roles.filter((role) => !role.slug.toLowerCase().includes('admin'));
      this.scopes = scopes.filter(
        (scope) => scope.guid !== process.env.REACT_APP_RECOGNITION_DESCOMPLICA_IO_SCOPE,
      );
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const dto: CreateSSORegistrationDto = {
        roleGuid: this.form.values.role.guid,
        scopeGuid: this.form.values.scope.guid,
        provider: this.form.values.provider,
        tag: this.form.values.tag,
      };

      await this.createSSORegistrationCollaboration.run(dto);

      this.form.reset();

      toast.success('Link de SSO criado com sucesso');
      this.navigate('/admin/sso/registrations');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar link de SSO. Tente novamente.');
    } finally {
      this.isSubmitting = false;
    }
  }
}
